import { useState, useRef, useMemo } from "react";
import { Copy, Upload, ImageIcon, Loader2, CheckCircle2, Info, Clock, AlertCircle, FileText } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import baridimobLogo from "@/assets/baridimob.png.asset.json";
import { useT } from "@/i18n/useT";


interface Props {
  plan: "starter" | "pro" | "unlimited";
  amountCents: number;
  userId: string | null | undefined;
  defaultSenderName?: string;
  onSubmitted?: () => void;
}

const BARIDIMOB_ACCOUNT = "00799999002814959897";
const BARIDIMOB_HOLDER = "Lumvio Payments";
const DZD_BY_PLAN: Record<"starter" | "pro" | "unlimited", number> = {
  starter: 2500,
  pro: 5000,
  unlimited: 8750,
};

export function BaridiMobPaymentCard({ plan, amountCents, userId, defaultSenderName, onSubmitted }: Props) {
  const dzdAmount = DZD_BY_PLAN[plan] ?? Math.round((amountCents / 100) * 250);
  const usdAmount = amountCents / 100;
  const tt = useT();
  const [displayCurrency, setDisplayCurrency] = useState<"DZD" | "USD">("DZD");
  const [senderName, setSenderName] = useState(defaultSenderName ?? "");
  const [note, setNote] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [touched, setTouched] = useState<{ senderName?: boolean; file?: boolean }>({});
  const [showAll, setShowAll] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const errors = useMemo(() => {
    const e: { senderName?: string; file?: string; note?: string } = {};
    const name = senderName.trim();
    if (!name) e.senderName = tt("Enter the sender name as it appears on BaridiMob.", "أدخل اسم المرسِل كما يظهر في بريدي موب.");
    else if (name.length < 3) e.senderName = tt("Name looks too short — use the full name.", "الاسم قصير جدًا — استخدم الاسم الكامل.");
    else if (name.length > 100) e.senderName = tt("Name must be under 100 characters.", "يجب أن يكون الاسم أقل من 100 حرف.");

    if (fileError) e.file = fileError;
    else if (!file) e.file = tt("Attach a screenshot or PDF receipt of the transfer.", "أرفق لقطة شاشة أو ملف PDF لإيصال التحويل.");

    if (note.trim().length > 500) e.note = tt("Note must be under 500 characters.", "يجب أن تكون الملاحظة أقل من 500 حرف.");
    return e;
  }, [senderName, file, fileError, note, tt]);

  const show = (k: "senderName" | "file") => (showAll || touched[k]) && errors[k];
  const hasErrors = Object.keys(errors).length > 0;

  function pickFile(f: File | null) {
    setTouched((t) => ({ ...t, file: true }));
    if (!f) return;
    const isImage = f.type.startsWith("image/");
    const isPdf = f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf");
    if (!isImage && !isPdf) {
      setFile(null);
      setPreview(null);
      return setFileError(tt("Unsupported file. Use PNG, JPG or PDF.", "ملف غير مدعوم. استخدم PNG أو JPG أو PDF."));
    }
    if (f.size > 8 * 1024 * 1024) {
      setFile(null);
      setPreview(null);
      return setFileError(tt("File is larger than 8 MB. Try a smaller one.", "حجم الملف أكبر من 8 ميغابايت. جرّب ملفًا أصغر."));
    }
    setFileError(null);
    setFile(f);
    setPreview(isImage ? URL.createObjectURL(f) : null);
  }


  async function copyAccount() {
    try {
      await navigator.clipboard.writeText(BARIDIMOB_ACCOUNT);
      toast.success(tt("Account number copied", "تم نسخ رقم الحساب"));
    } catch {
      toast.error(tt("Copy failed", "فشل النسخ"));
    }
  }

  async function submit() {
    if (!userId) return toast.error(tt("Sign in first", "سجّل الدخول أولاً"));
    setShowAll(true);
    if (hasErrors) {
      return toast.error(tt("Please fix the highlighted fields.", "يرجى تصحيح الحقول المحددة."));
    }
    setSubmitting(true);
    try {
      const isPdf = file!.type === "application/pdf" || file!.name.toLowerCase().endsWith(".pdf");
      const ext = file!.name.split(".").pop()?.toLowerCase() || (isPdf ? "pdf" : "jpg");
      const path = `${userId}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const bucket = isPdf ? "payment-proofs-pdf" : "payment-proofs";
      const up = await supabase.storage.from(bucket).upload(path, file!, {
        contentType: file!.type || (isPdf ? "application/pdf" : "image/jpeg"),
        upsert: false,
      });
      if (up.error) throw up.error;

      const { error } = await supabase.from("manual_payments").insert({
        user_id: userId,
        plan,
        amount_cents: amountCents,
        currency: "USD",
        method: "baridimob",
        proof_path: path,
        sender_name: senderName.trim(),
        sender_note: note.trim() || null,
      });
      if (error) throw error;

      setDone(true);
      toast.success(tt("Proof submitted. Waiting for admin review.", "تم إرسال الإثبات. بانتظار مراجعة الإدارة."));
      onSubmitted?.();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setSubmitting(false);
    }
  }


  if (done) {
    return (
      <div className="rounded-2xl border border-yellow-400/20 bg-yellow-400/[0.06] p-6 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-yellow-400/15">
          <Clock className="h-7 w-7 text-yellow-500" />
        </div>
        <h3 className="mt-3 text-lg font-semibold">
          {tt("Transfer submitted", "تم إرسال التحويل")}
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {tt(
            "Our team will verify your BaridiMob transfer and activate your plan within 24 hours. You'll get an email once approved.",
            "سيتحقق فريقنا من تحويل بريدي موب وسيُفعّل خطتك خلال 24 ساعة. ستصلك رسالة عند الموافقة.",
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* BaridiMob payment card */}
      <div className="relative overflow-hidden rounded-2xl border border-border/50 bg-card p-5 sm:p-6">
        {/* Header */}
        <div className="relative flex items-center gap-4">
          <div className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-yellow-400/15 p-2.5 ring-1 ring-yellow-400/30">
            <img src={baridimobLogo.url} alt="BaridiMob" className="relative h-full w-full object-contain" />
          </div>
          <div>
            <div className="text-base font-semibold text-foreground">
              {tt("Pay with BaridiMob", "الدفع عبر بريدي موب")}
            </div>
            <p className="mt-0.5 max-w-md text-sm text-muted-foreground">
              {tt(
                "Transfer the amount, then upload the confirmation screenshot. We activate your plan within 24h after review.",
                "قم بتحويل المبلغ ثم ارفع لقطة شاشة التأكيد. سنُفعّل خطتك خلال 24 ساعة بعد المراجعة.",
              )}
            </p>
          </div>
        </div>

        {/* Amount block */}
        <div className="mt-5 rounded-2xl border border-border/50 bg-muted/30 p-4 sm:p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
                {tt("Amount to transfer", "المبلغ المطلوب تحويله")}
              </div>
              <div className="mt-1 flex items-baseline gap-3 flex-wrap">
                <span className="text-3xl font-bold tracking-tight text-foreground tabular-nums">
                  {displayCurrency === "DZD" ? (
                    <>{dzdAmount.toLocaleString("en-US")} <span className="text-xl font-semibold">DZD</span></>
                  ) : (
                    <>${usdAmount.toFixed(2)} <span className="text-xl font-semibold">USD</span></>
                  )}
                </span>
                <span className="inline-flex items-center rounded-full border border-border/50 bg-muted/50 px-2 py-0.5 text-xs font-medium text-muted-foreground">
                  {tt("Chosen currency", "العملة المختارة")}
                </span>
              </div>
              <div className="mt-1 text-sm text-muted-foreground tabular-nums">
                {tt("Equivalent", "المعادل")}:{" "}
                {displayCurrency === "DZD"
                  ? <>${usdAmount.toFixed(2)} USD</>
                  : <>{dzdAmount.toLocaleString("en-US")} DZD</>}
              </div>
            </div>

            {/* Currency toggle */}
            <div className="flex flex-col items-start gap-1.5 sm:items-end">
              <div className="inline-flex rounded-full border border-border/50 bg-background p-1">
                {(["DZD", "USD"] as const).map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setDisplayCurrency(c)}
                    className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
                      displayCurrency === c
                        ? "bg-foreground text-background"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <div className="text-[11px] text-muted-foreground">{plan} / {tt("month", "شهريًا")}</div>
            </div>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            {tt(
              "BaridiMob transfers must be sent in DZD — always send the DZD amount above.",
              "تحويلات بريدي موب تكون بالدينار الجزائري — أرسل دائمًا المبلغ بالدينار المذكور أعلاه.",
            )}
          </p>
        </div>

        {/* Account number block */}
        <div className="mt-4 rounded-2xl border border-border/50 bg-muted/30 p-4 sm:p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
                {tt("BaridiMob account number", "رقم حساب بريدي موب")}
              </div>
              <div className="mt-1 font-mono text-xl font-semibold tracking-wider text-foreground">
                {BARIDIMOB_ACCOUNT}
              </div>
              <div className="mt-0.5 text-sm text-muted-foreground">{BARIDIMOB_HOLDER}</div>
            </div>
            <Button
              type="button"
              size="sm"
              onClick={copyAccount}
              className="h-10 gap-2 rounded-full bg-foreground text-background hover:bg-foreground/90"
            >
              <Copy className="h-4 w-4" /> {tt("Copy number", "نسخ الرقم")}
            </Button>
          </div>
        </div>

        {/* Note */}
        <div className="mt-4 flex items-start gap-3 rounded-xl border border-border/50 bg-muted/30 p-3 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {tt(
              "Include your account email in the BaridiMob transfer note so we can match your payment faster.",
              "اذكر بريدك الإلكتروني في ملاحظة تحويل بريدي موب لتسريع مطابقة الدفع.",
            )}
          </span>
        </div>
      </div>

      {/* Proof upload form */}
      <div className="space-y-3 rounded-2xl border border-border/50 bg-card p-4">
        <div className="space-y-1.5">
          <Label htmlFor="mp-sender">{tt("Sender name (as on BaridiMob)", "اسم المرسِل (كما في بريدي موب)")}</Label>
          <Input
            id="mp-sender"
            value={senderName}
            onChange={(e) => setSenderName(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, senderName: true }))}
            placeholder="Ahmed Benali"
            maxLength={100}
            aria-invalid={Boolean(show("senderName"))}
            aria-describedby="mp-sender-msg"
            className={cn(show("senderName") && "border-destructive focus-visible:ring-destructive")}
          />
          <p
            id="mp-sender-msg"
            className={cn("text-[11px]", show("senderName") ? "text-destructive" : "text-muted-foreground")}
          >
            {show("senderName")
              ? errors.senderName
              : tt("Must match the name on the transfer receipt.", "يجب أن يطابق الاسم في إيصال التحويل.")}
          </p>
        </div>

        <div className="space-y-1.5">
          <Label>{tt("Transfer proof (screenshot or PDF)", "إثبات التحويل (لقطة شاشة أو PDF)")}</Label>
          <input
            ref={inputRef}
            type="file"
            accept="image/*,application/pdf,.pdf"
            className="hidden"
            onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
          />
          {preview ? (
            <div className="relative overflow-hidden rounded-xl border border-border/50 bg-muted/30">
              <img src={preview} alt="proof preview" className="max-h-64 w-full object-contain" />
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="absolute bottom-2 end-2"
                onClick={() => inputRef.current?.click()}
              >
                <Upload className="h-3.5 w-3.5" /> {tt("Change", "تغيير")}
              </Button>
            </div>
          ) : file ? (
            <div className="flex items-center gap-3 rounded-xl border border-border/50 bg-muted/30 p-3">
              <FileText className="h-6 w-6 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{file.name}</div>
                <div className="text-[11px] text-muted-foreground">
                  PDF · {(file.size / (1024 * 1024)).toFixed(1)} MB
                </div>
              </div>
              <Button type="button" size="sm" variant="secondary" onClick={() => inputRef.current?.click()}>
                <Upload className="h-3.5 w-3.5" /> {tt("Change", "تغيير")}
              </Button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              aria-invalid={Boolean(show("file"))}
              aria-describedby="mp-file-msg"
              className={cn(
                "flex w-full items-center justify-center gap-2 rounded-xl border border-dashed bg-muted/30 py-8 text-sm transition-colors",
                show("file")
                  ? "border-destructive/60 text-destructive"
                  : "border-border/50 text-muted-foreground hover:border-primary/40 hover:text-foreground",
              )}
            >
              <ImageIcon className="h-5 w-5" />
              {tt("Click to upload proof (PNG, JPG, PDF · max 8 MB)", "اضغط لرفع الإثبات (PNG أو JPG أو PDF · حد أقصى 8 ميغا)")}
            </button>
          )}

          {show("file") && (
            <p id="mp-file-msg" className="flex items-center gap-1.5 text-[11px] text-destructive">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {errors.file}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="mp-note">{tt("Note for the admin (optional)", "ملاحظة للمشرف (اختياري)")}</Label>
          <Textarea
            id="mp-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={500}
            placeholder={tt("Transaction reference, time of transfer…", "المرجع، وقت التحويل…")}
          />
          <p className="text-end text-[11px] text-muted-foreground tabular-nums">{note.length}/500</p>
        </div>

        {showAll && hasErrors && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-xl border border-destructive/30 bg-destructive/[0.06] p-3 text-xs text-destructive"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <div className="font-semibold">
                {tt("Please fix before submitting:", "يرجى التصحيح قبل الإرسال:")}
              </div>
              <ul className="mt-1 list-disc space-y-0.5 ps-4">
                {Object.values(errors).map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <Button
          type="button"
          onClick={submit}
          disabled={submitting || !userId}
          className="group relative h-12 w-full overflow-hidden rounded-full bg-foreground text-background font-semibold tracking-tight transition-all hover:bg-foreground/90 active:scale-[0.99] disabled:opacity-50"
        >
          {submitting ? (
            <><Loader2 className="me-2 h-4 w-4 animate-spin" /> {tt("Submitting…", "جارٍ الإرسال…")}</>
          ) : (
            <><CheckCircle2 className="me-2 h-4 w-4" /> {tt("Submit proof for review", "أرسِل الإثبات للمراجعة")}</>
          )}
        </Button>


        <p className="text-center text-[11px] text-muted-foreground">
          {tt("Plan activates within 24h after admin approval.", "تُفعَّل الخطة خلال 24 ساعة بعد موافقة المشرف.")}
        </p>
      </div>
    </div>
  );
}
